import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function AnalyticsPage({ user, setUser }) {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      
      // Fetch user transactions
      const tradesRes = await axios.get(`${API}/transactions`, config);
      const allTrades = tradesRes.data || [];
      setTrades(allTrades);
      
      // Calculate stats
      const totalTrades = allTrades.length;
      const buyTrades = allTrades.filter(t => t.transaction_type === 'BUY').length;
      const sellTrades = allTrades.filter(t => t.transaction_type === 'SELL').length;
      const totalVolume = allTrades.reduce((sum, t) => sum + (t.total || 0), 0);
      
      setStats({
        totalTrades,
        buyTrades,
        sellTrades,
        totalVolume,
        avgTradeSize: totalTrades > 0 ? totalVolume / totalTrades : 0
      });
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Analiz Merkezi</h1>
          <p className="text-gray-400 text-sm sm:text-base">İşlem istatistikleriniz ve performans analizi</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-xl">Yükleniyor...</div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl mb-2"></div>
                <div className="text-2xl sm:text-3xl font-bold">{stats?.totalTrades || 0}</div>
                <div className="text-sm text-blue-200">Toplam İşlem</div>
              </div>
              
              <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-lg p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl mb-2"></div>
                <div className="text-2xl sm:text-3xl font-bold">{stats?.buyTrades || 0}</div>
                <div className="text-sm text-green-200">Alış İşlemi</div>
              </div>
              
              <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-lg p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl mb-2"></div>
                <div className="text-2xl sm:text-3xl font-bold">{stats?.sellTrades || 0}</div>
                <div className="text-sm text-red-200">Satış İşlemi</div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl mb-2"></div>
                <div className="text-xl sm:text-2xl font-bold">₺{(stats?.totalVolume || 0).toFixed(2)}</div>
                <div className="text-sm text-purple-200">Toplam Hacim</div>
              </div>
            </div>

            {/* Recent Trades */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-700">
                <h2 className="text-lg sm:text-xl font-bold">Son İşlemler</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-750">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tarih</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Varlık</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tip</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Miktar</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fiyat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {trades.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                          Henüz işlem yapılmadı
                        </td>
                      </tr>
                    ) : (
                      trades.slice(0, 10).map((trade, idx) => (
                        <tr key={idx} className="hover:bg-gray-750">
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm whitespace-nowrap">
                            {new Date(trade.timestamp).toLocaleString('tr-TR')}
                          </td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm font-medium">{trade.asset_id}</td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              trade.side === 'BUY' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                            }`}>
                              {trade.side === 'BUY' ? 'ALIŞ' : 'SATIŞ'}
                            </span>
                          </td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm">{trade.amount}</td>
                          <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm font-mono">₺{trade.price.toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
